import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForwardIosOutlined } from "@mui/icons-material";
import { ENV_REACT_APP } from "../../Configurations/ENV_Config";

interface ApiResponse {
  isApiError: boolean;
  data: {
    message: string;
    isAlreadySubscribed?: boolean;
    isNewSubscription?: boolean;
  };
  errors: {
    errorCode: string;
    message: string;
  };
}

const NewsletterSignup = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "info" | "error";
    message: string;
  } | null>(null);

  // Split name into firstName and lastName
  const splitName = (fullName: string) => {
    const trimmedName = fullName.trim();
    const parts = trimmedName.split(/\s+/);

    if (parts.length === 1) {
      // Only first name provided
      return { firstName: parts[0], lastName: "" };
    }

    // First part is firstName, rest is lastName
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");
    return { firstName, lastName };
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setStatus({
        type: "error",
        message: "Please enter your name.",
      });
      return;
    }

    if (!email || !email.includes("@")) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    const { firstName, lastName } = splitName(name);

    try {
      const response = await fetch(
        `${ENV_REACT_APP.REACT_APP_API_BASE_URL}/api/newsletter/subscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, firstName, lastName }),
        }
      );

      const result: ApiResponse = await response.json();

      if (result.isApiError) {
        setStatus({
          type: "error",
          message: result.errors.message || "Something went wrong. Please try again.",
        });
        return;
      }

      // Handle already subscribed case
      if (result.data.isAlreadySubscribed) {
        setStatus({
          type: "info",
          message: result.data.message,
        });
      } else {
        // New subscription - clear form
        setStatus({
          type: "success",
          message: result.data.message,
        });
        setName("");
        setEmail("");
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const getStatusColor = () => {
    switch (status?.type) {
      case "success":
        return "#90EE90"; // Light green
      case "info":
        return "#87CEEB"; // Light blue
      case "error":
        return "#FFB6C1"; // Light pink/red
      default:
        return "#FFFFFF";
    }
  };

  const textFieldStyles = {
    backgroundColor: "white",
    borderRadius: 2,
    "& .MuiOutlinedInput-root": {
      ".MuiInputBase-input": {
        padding: "12px 30px",
      },
      "& input::placeholder": {
        color: "#444",
        fontSize: "1.1rem",
        fontWeight: 500,
        opacity: 1,
      },
    },
  };

  return (
    <Box
      px={10}
      pb={15}
      pt={13}
      bgcolor={"#1c4c4d"}
      borderRadius={5}
      width={{
        xl: "400px",
        lg: "400px",
        md: "auto",
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h5" sx={{ color: "white" }} fontWeight={"500"}>
          Join Now
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ color: "white" }}
          fontWeight={"500"}
        >
          Subscribe for updates
        </Typography>
      </Stack>
      <Stack mt={3} spacing={3}>
        <TextField
          id="newsletter-name-input"
          sx={textFieldStyles}
          fullWidth
          placeholder="Your Name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyUp={handleKeyUp}
          disabled={isSubmitting}
        />
        <TextField
          sx={textFieldStyles}
          fullWidth
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyUp={handleKeyUp}
          disabled={isSubmitting}
        />
        <Button
          variant="contained"
          color="secondary"
          size="large"
          sx={{ justifyContent: "space-between" }}
          onClick={handleSubmit}
          disabled={isSubmitting}
          endIcon={
            isSubmitting ? (
              <CircularProgress size={15} sx={{ color: "white" }} />
            ) : (
              <ArrowForwardIosOutlined
                sx={{
                  color: "white",
                  height: "15px",
                  width: "15px",
                }}
              />
            )
          }
        >
          <Typography
            variant="subtitle1"
            fontSize={"1.1rem"}
            fontWeight={400}
            justifyContent={"space-between"}
            display={"flex"}
            sx={{ color: "white", fontSize: "1.1rem" }}
          >
            {isSubmitting ? "Signing Up..." : "Sign Up"}
          </Typography>
        </Button>
        {status && (
          <Typography
            variant="body2"
            sx={{
              color: getStatusColor(),
              fontWeight: 500,
            }}
          >
            {status.message}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default NewsletterSignup;
